import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useCatalog } from '@/context/CatalogContext';
import { usePageContent } from '@/context/ContentContext';
import { telHref } from '@/lib/phone';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const megaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { totalItems } = useCart();
  const { categories } = useCatalog();
  const { phone } = usePageContent('contact');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMegaEnter = () => {
    if (megaTimeoutRef.current) clearTimeout(megaTimeoutRef.current);
    setIsMegaOpen(true);
  };

  const handleMegaLeave = () => {
    megaTimeoutRef.current = setTimeout(() => {
      setIsMegaOpen(false);
    }, 150);
  };

  const navLinks = [
    { name: 'Kućni brojevi', href: '/kucni-brojevi' },
    { name: 'Pločice za vrata', href: '/plocice-za-vrata' },
    { name: 'Proizvodi', href: '/shop', hasMega: true },
    { name: 'Za Firme', href: '/za-firme' },
    { name: 'Blog', href: '/#blog' },
    { name: 'O nama', href: '/#about' },
    { name: 'Kontakt', href: '/#contact' },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#1a1a1a] border-b border-white/10">
        <div className="w-full px-4 md:px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            {phone && (
              <a
                href={telHref(phone)}
                className="flex items-center gap-1.5 text-xs md:text-sm font-label font-semibold text-amber-400 hover:text-amber-300 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-fill text-xs md:text-sm" />
                {phone}
              </a>
            )}
            <div className="hidden sm:flex items-center gap-3">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white transition-colors">
                <i className="ri-facebook-fill text-xs" />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white transition-colors">
                <i className="ri-instagram-line text-xs" />
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-white transition-colors">
                <i className="ri-tiktok-fill text-xs" />
              </a>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-label text-white/70">
            <span className="hidden sm:inline">Preko 35 000 zadovoljnih korisnika</span>
            <div className="flex items-center gap-0.5">
              <i className="ri-star-fill text-amber-400 text-xs" />
              <i className="ri-star-fill text-amber-400 text-xs" />
              <i className="ri-star-fill text-amber-400 text-xs" />
              <i className="ri-star-fill text-amber-400 text-xs" />
              <i className="ri-star-fill text-amber-400 text-xs" />
            </div>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className={`relative sticky top-0 z-50 bg-white border-b border-background-200/50 transition-shadow duration-300 ${isScrolled ? 'shadow-sm' : ''}`}>
        <div className="w-full px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-600 rounded">
              <i className="ri-home-4-line text-white text-lg" />
            </div>
            <span className="font-heading text-xl font-semibold tracking-tight text-foreground-950">
              Kućni Brojevi
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={link.hasMega ? handleMegaEnter : undefined}
                onMouseLeave={link.hasMega ? handleMegaLeave : undefined}
              >
                <Link
                  to={link.href}
                  className="text-sm font-label text-foreground-700 hover:text-amber-600 transition-colors whitespace-nowrap"
                >
                  {link.name}
                </Link>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* KONFIGURATOR button hidden for now - see router/config.tsx to re-enable */}

            <button className="w-10 h-10 flex items-center justify-center text-foreground-600 hover:text-amber-600 transition-colors">
              <i className="ri-search-line text-lg" />
            </button>
            <Link to="/cart" className="w-10 h-10 flex items-center justify-center text-foreground-600 hover:text-amber-600 transition-colors relative">
              <i className="ri-shopping-cart-line text-lg" />
              {totalItems > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-amber-600 text-white text-[10px] font-label rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>
            <button className="w-10 h-10 flex items-center justify-center text-foreground-600 hover:text-amber-600 transition-colors">
              <i className="ri-user-line text-lg" />
            </button>
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`} />
            </button>
          </div>
        </div>

        {/* Mega Menu Dropdown */}
        {isMegaOpen && (
          <div
            className="hidden md:block absolute left-0 right-0 bg-white border-b border-background-200 shadow-lg z-40"
            onMouseEnter={handleMegaEnter}
            onMouseLeave={handleMegaLeave}
          >
            <div className="w-full px-4 md:px-6 py-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-semibold text-foreground-950">Kategorije proizvoda</h3>
                <Link to="/shop" className="text-sm font-label text-amber-600 hover:text-amber-700 transition-colors flex items-center gap-1">
                  Pogledaj sve
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-4 lg:grid-cols-8 gap-4">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/shop?category=${category.slug}`}
                    className="group flex flex-col items-center gap-2 text-center"
                  >
                    <div className="w-16 h-16 flex items-center justify-center rounded-full overflow-hidden border border-background-200 group-hover:border-amber-500 transition-colors bg-background-100 text-foreground-700 group-hover:text-amber-600 font-label text-sm font-semibold uppercase">
                      {category.name.charAt(0)}
                    </div>
                    <span className="text-xs font-label text-foreground-700 group-hover:text-amber-600 transition-colors leading-tight">
                      {category.name}
                    </span>
                  </Link>
                ))}
              </div>
              {/* Configurator CTA hidden for now - see router/config.tsx to re-enable */}
            </div>
          </div>
        )}

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-background-200/30 px-4 py-4">
            {phone && (
              <a
                href={telHref(phone)}
                className="flex items-center justify-center gap-2 mb-3 py-3 bg-amber-600 text-white font-label text-sm font-semibold rounded-full"
              >
                <i className="ri-phone-fill" />
                Pozovi {phone}
              </a>
            )}
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className="block py-3 text-sm font-label text-foreground-700 hover:text-amber-600 transition-colors border-b border-background-200/30 last:border-0"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/cart" className="block py-3 text-sm font-label text-foreground-700 hover:text-amber-600 transition-colors border-b border-background-200/30">
              Korpa ({totalItems})
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}